import { useController, ApplicationView } from '@aha-app/mvc';
import classNames from 'classnames';
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import Category from './Category';
import EmptyTemplateSearchResult from './EmptyTemplateSearchResult';
import styles from './AiTemplatesModal.module.css';
import SearchBox from './SearchBox';
import TemplateTypeSelector from './TemplateTypeSelector';
import AiTemplateController, {
  TemplateCategories,
  TemplateTypes,
  agentTypeNames,
} from './AiTemplateController';
import type { AiUserAgent } from 'components/framework/models';
import type { RecordId } from 'javascripts/shared-types';
import bindHotkey, { unbindHotkey } from 'javascripts/keybindings/bindHotkey';
import defaultPromptIcon from 'images/ai_user_agents/templates/prompt.svg';
import defaultInstructionIcon from 'images/ai_user_agents/templates/instruction.svg';
import defaultAgentIcon from 'images/ai_user_agents/templates/agent.svg';
import SharedDndProvider from 'components/DragDropContext/SharedDndProvider';
import SortableFavoriteRow from './SortableFavoriteRow';
import { generateKeyBetween } from 'fractional-indexing';
import AhaAnalytics from 'javascripts/analytics';

export const defaultTemplateIcon = type => {
  switch (type) {
    case TemplateTypes.PROMPT:
      return defaultPromptIcon;
    case TemplateTypes.INSTRUCTION:
      return defaultInstructionIcon;
    default:
      return defaultAgentIcon;
  }
};

const AiTemplatePicker = () => {
  const controller = useController(AiTemplateController);
  const { templates, currentCategory, templateType, favoriteAgents } =
    controller.state;
  const [filterText, setFilterText] = useState('');
  const [searchBoxKey, setSearchBoxKey] = useState(1);
  const defaultTitle = (
    <span className={styles.defaultTitle}>
      <aha-ai-icon /> AI prompt library
    </span>
  );
  let title = defaultTitle as string | React.ReactNode | undefined;
  const categories = [...controller.categories];

  let filteredTemplates: Array<AiUserAgent> = [];

  useEffect(() => {
    if (controller) {
      bindHotkey('ESC', () => {
        controller.props.closeTemplateModal?.() ||
          controller.actionAbortChooseTemplate();
      });
      return () => {
        unbindHotkey('ESC');
      };
    }
  }, [controller]);

  if (filterText.length > 0) {
    const lowerFilterText = filterText.toLowerCase();
    const allTemplates = [
      ...templates.workspace,
      ...templates.personal,
      ...templates.global,
    ];
    filteredTemplates = allTemplates.filter(template =>
      template.name?.toLowerCase().includes(lowerFilterText)
    );
    if (filteredTemplates.length === 0) {
      title = `No results for ${filterText}`;
    } else {
      const pluralize = filteredTemplates.length > 1 ? 'results' : 'result';
      title = `${filteredTemplates.length} ${pluralize} for ${filterText}`;
    }
  } else {
    if (currentCategory === TemplateCategories.All) {
      filteredTemplates = [...templates.workspace, ...templates.personal];
    } else if (currentCategory === TemplateCategories.Workspace) {
      filteredTemplates = templates.workspace;
    } else if (currentCategory === TemplateCategories.Personal) {
      filteredTemplates = templates.personal;
    } else if (currentCategory === TemplateCategories.Favorites) {
      filteredTemplates = [
        ...templates.workspace,
        ...templates.personal,
      ].filter(template =>
        favoriteAgents.some(fav => fav.aiUserAgent.id === template.id)
      );
    } else if (currentCategory === TemplateCategories.Global_All) {
      filteredTemplates = templates.global;
    } else {
      filteredTemplates = templates.global.filter(
        template =>
          currentCategory && template.categories?.includes(currentCategory)
      );
    }

    title =
      currentCategory === TemplateCategories.All ||
      currentCategory === TemplateCategories.Global_All
        ? defaultTitle
        : currentCategory;
  }

  filteredTemplates = useMemo(() => {
    const filterByTemplateType = (templateList: Array<AiUserAgent>) => {
      if (templateType === TemplateTypes.ALL) {
        return templateList;
      }
      return templateList.filter(
        template => template.agentType === templateType
      );
    };

    const sorted = filterByTemplateType(filteredTemplates);

    // Sort favorites by position, others by name
    if (currentCategory === TemplateCategories.Favorites) {
      return sorted.sort((a, b) => {
        const favA = favoriteAgents.find(fav => fav.aiUserAgent.id === a.id);
        const favB = favoriteAgents.find(fav => fav.aiUserAgent.id === b.id);

        if (favA?.position && favB?.position) {
          return (favA.position || '') < (favB.position || '') ? -1 : 1;
        }
        return 0;
      });
    }

    return sorted;
  }, [filteredTemplates, templateType, currentCategory, favoriteAgents]);

  const onCategoryClick = category => {
    setFilterText('');
    setSearchBoxKey(searchBoxKey + 1);
    controller.actionSetCategory(category);
  };

  const showNewTemplateButton =
    currentCategory === TemplateCategories.Personal ||
    (currentCategory === TemplateCategories.Workspace &&
      window.currentUserRole === 'product_owner');

  const moveRow = useCallback(
    async (dragId: RecordId, hoverIndex: number) => {
      if (parseInt(dragId) === hoverIndex) return;

      const draggedItem = favoriteAgents.find(fav => fav.id === dragId);
      if (!draggedItem) return;

      let newPosition: string;

      if (hoverIndex === 0) {
        // Moving to first position
        const firstItem = favoriteAgents[0];
        newPosition = generateKeyBetween(null, firstItem?.position || null);
      } else if (hoverIndex >= favoriteAgents.length - 1) {
        // Moving to last position
        const lastItem = favoriteAgents[favoriteAgents.length - 1];
        newPosition = generateKeyBetween(lastItem?.position || null, null);
      } else {
        // Moving between two items
        const beforeItem = favoriteAgents[hoverIndex - 1];
        const afterItem = favoriteAgents[hoverIndex];
        newPosition = generateKeyBetween(
          beforeItem?.position || null,
          afterItem?.position || null
        );
      }

      await controller.actionReorderFavorite(draggedItem.id, newPosition);
    },
    [favoriteAgents, controller]
  );

  return (
    <div className={styles.templateModal} data-skip-drawer-close>
      <div className={styles.leftSide}>
        <div className={styles.leftSideScrollable}>
          <div className={styles.searchPanel}>
            <SearchBox
              key={searchBoxKey}
              placeholder={'Search all prompts'}
              onChange={value => setFilterText(value)}
            />
          </div>
          <div className={styles.templateTypePanel}>
            <TemplateTypeSelector
              selectedType={templateType}
              onTypeChange={type => controller.actionSetTemplateType(type)}
            />
          </div>
          {!controller.props.hideMyInputs && (
            <div>
              <div className={styles.heading}>My prompts</div>
              {[
                TemplateCategories.All,
                TemplateCategories.Workspace,
                TemplateCategories.Personal,
                TemplateCategories.Favorites,
              ].map(category => (
                <Category
                  key={category}
                  category={category}
                  selected={category === currentCategory}
                  onClick={category => onCategoryClick(category)}
                />
              ))}
            </div>
          )}
          {categories.length > 0 && (
            <>
              <div className={styles.heading}>More prompts</div>
              {categories.map(category => (
                <Category
                  key={category}
                  category={category}
                  selected={category === currentCategory}
                  onClick={category => onCategoryClick(category)}
                />
              ))}
            </>
          )}
        </div>

        <div className={styles.createTemplateCTA}>
          <div className={styles.createTemplateCTATitle}>Custom prompts</div>
          <div className={styles.createTemplateCTADescription}>
            Add personal requests, instructions, and agents.
          </div>
          <a href={`/settings/ai_agents?tab=${templateType.toLowerCase()}s`}>
            <aha-button kind='secondary' size='small'>
              <i className='fa-regular fa-plus' />
              Create custom prompt
            </aha-button>
          </a>
        </div>
      </div>
      <div className={styles.rightSide}>
        <div className={styles.templateHeader}>
          <div className={styles.templateHeaderTitle}>
            {title}
            {showNewTemplateButton && (
              <a
                href={
                  currentCategory === TemplateCategories.Personal
                    ? `/settings/ai_agents?tab=${templateType.toLowerCase()}s`
                    : `/settings/projects/${
                        window.currentProject?.referencePrefix
                      }/ai_agents?tab=${templateType.toLowerCase()}s`
                }
              >
                <aha-button
                  kind='secondary'
                  size='small'
                  style={{
                    marginLeft: '16px',
                  }}
                >
                  <i className='fa-regular fa-plus' />
                  New
                </aha-button>
              </a>
            )}
          </div>
          <aha-icon
            class={styles.closeButton}
            icon='far fa-close'
            onClick={() => controller.actionAbortChooseTemplate()}
          />
        </div>
        <div className={styles.templatesPanel}>
          {currentCategory === TemplateCategories.Favorites ? (
            <SharedDndProvider>
              <div className={styles.favoritesSection}>
                <table className={styles.favoritesTable}>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTemplates.map((template, index) => (
                      <SortableFavoriteRow
                        key={template.id}
                        index={index}
                        template={template}
                        favoriteId={
                          favoriteAgents.find(
                            fav => fav.aiUserAgent.id === template.id
                          )?.id || ('0' as RecordId)
                        }
                        moveRow={moveRow}
                        onToggleFavorite={template =>
                          void controller.actionToggleFavorite(template)
                        }
                        onPlaceTemplate={template =>
                          void controller.actionPlaceTemplate(
                            template,
                            'AI library tile',
                            'My prompts'
                          )
                        }
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </SharedDndProvider>
          ) : (
            <div className={styles.templatesList}>
              {filteredTemplates.length > 0 &&
                filteredTemplates.map(template => {
                  const templateButton = (
                    <aha-button
                      style={{
                        width: '100%',
                        height: '32px',
                        cursor: 'pointer',
                      }}
                      data-skip-drawer-close
                      onClick={event => {
                        event.stopPropagation();
                        event.preventDefault();

                        void controller.actionPlaceTemplate(
                          template,
                          'AI library tile',
                          template.agentScope !== 'GLOBAL'
                            ? 'My prompts'
                            : 'More prompts'
                        );
                      }}
                      kind='primary'
                    >
                      {controller.props.placeTemplate ? 'Use' : 'Add'}{' '}
                      {agentTypeNames[template.agentType]}{' '}
                      <aha-icon icon='far fa-right-long' />
                    </aha-button>
                  );

                  const isFavorite = controller.isFavorite(template.id);

                  return (
                    <div
                      className={styles.templateTileContainer}
                      key={template.id}
                    >
                      <div className={styles.templateTile}>
                        {template.agentScope !== 'GLOBAL' && (
                          <div
                            className={classNames(styles.favoriteButton, {
                              [styles.favorited]: isFavorite,
                            })}
                          >
                            <svg
                              xmlns='http://www.w3.org/2000/svg'
                              width='39'
                              height='35'
                              viewBox='0 0 39 35'
                              fill='none'
                              className={styles.favoriteIconBackground}
                            >
                              <path
                                d='M38.0435 0L0 35V3C0 1.34315 1.34315 0 3 0H38.0435Z'
                                fill='#F7F7F7'
                              />
                            </svg>
                            <aha-icon
                              icon={isFavorite ? 'fa fa-star' : 'far fa-star'}
                              onClick={event => {
                                event.stopPropagation();
                                void controller.actionToggleFavorite(template);
                              }}
                              style={{
                                color: isFavorite
                                  ? 'var(--aha-yellow-400)'
                                  : 'var(--theme-tertiary-text)',
                                cursor: 'pointer',
                                transition: 'color 0.2s ease',
                              }}
                              class={styles.favoriteIcon}
                            />
                          </div>
                        )}
                        <div className={classNames(styles.templateTilePreview)}>
                          <img
                            src={
                              template.thumbnail ||
                              defaultTemplateIcon(template.agentType)
                            }
                            width='196'
                            height='130'
                            style={{ height: '130px' }}
                          />
                        </div>
                        <div className={classNames(styles.templateTileInfo)}>
                          <div
                            className={styles.templateTileCaption}
                            // variable name indicates HTML; setter is responsbile for safety
                            // eslint-disable-next-line react/no-danger
                            dangerouslySetInnerHTML={{
                              __html: template.shortDescription?.htmlBody ?? '',
                            }}
                          />
                          <div>
                            {templateButton}
                            {template.agentScope === 'GLOBAL' && (
                              <aha-button
                                style={{
                                  width: '100%',
                                  height: '32px',
                                  cursor: 'pointer',
                                  color: 'var(--aha-blue-500)',
                                  marginTop: '12px',
                                }}
                                onClick={event => {
                                  event.stopPropagation();
                                  event.preventDefault();

                                  controller.actionSetCurrentTemplate(template);

                                  AhaAnalytics.track(
                                    'Feature-7530436202602257500',
                                    'User previews library prompt',
                                    {
                                      'AI prompt type':
                                        agentTypeNames[
                                          template.agentType
                                        ].capitalize(),
                                      'Template category':
                                        template.categories?.join(', '),
                                      'Prompt name': template.name,
                                      'CTA used': 'See details',
                                    }
                                  );
                                }}
                              >
                                See details
                              </aha-button>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className={styles.templateTileName}>
                        {template.name}
                      </div>
                      <div className={styles.templateTileTag}>
                        {agentTypeNames[template.agentType]}
                      </div>
                    </div>
                  );
                })}
            </div>
          )}
          {filterText.length > 0 && filteredTemplates.length === 0 && (
            <EmptyTemplateSearchResult />
          )}
        </div>
      </div>
    </div>
  );
};

export default ApplicationView(AiTemplatePicker);
